import type { UseFormReturn } from 'react-hook-form';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useConditions, useCustomConditions } from '~/api/conditions';
import { Button, Dialog } from '~/components';
import { Field, Radio, Select, Textarea } from '~/components/Form';
import { srOnlyStyle } from '~/styles/utilities.css';

import { ConditionPdfDialog } from '../../ConditionPdfDialog/ConditionPdfDialog';
import { actionsStyle, cardSelectStyle, cardsRowStyle, fieldStyle, sectionStyle } from './CopyrightConditionCards.css';
import { FormCustomAgreement } from './FormCustomAgreement/FormCustomAgreement';

const CONDITION_PREFIX = 'condition:';
const CUSTOM_CONDITION_VALUE = 'customCondition';

type FormWithCondition = {
  condition: { id: number; type: 'condition' | 'customAgreement' | 'customCondition' } | null;
  contactId: number | null;
  specialConditions: string;
};

type Props<T extends FormWithCondition> = {
  form: T extends FormWithCondition ? UseFormReturn<T> : never;
  required?: boolean;
};

export const CopyrightConditionCards = <T extends FormWithCondition>({ form, required = true }: Props<T>) => {
  const { control, getValues, setValue, watch } = form;
  const { data: conditions } = useConditions();
  const { data: customConditions } = useCustomConditions();
  const { t } = useTranslation(['invoices', 'validation']);

  const [showSpecialConditions, setShowSpecialConditions] = useState(() => !!getValues('specialConditions'));

  const customConditionItems = useMemo(
    () => customConditions.map((customCondition) => ({ label: customCondition.name, value: String(customCondition.id) })),
    [customConditions],
  );

  const watchCondition = watch('condition');
  const isCustomAgreement = watchCondition?.type === 'customAgreement';

  const toggleSpecialConditions = () => {
    setShowSpecialConditions((previous) => {
      if (previous) setValue('specialConditions', '');
      return !previous;
    });
  };

  const wasCustomAgreementRef = useRef(isCustomAgreement);
  const showSpecialConditionsRef = useRef(showSpecialConditions);
  showSpecialConditionsRef.current = showSpecialConditions;

  useEffect(() => {
    const wasCustomAgreement = wasCustomAgreementRef.current;
    wasCustomAgreementRef.current = isCustomAgreement;

    if (wasCustomAgreement && !isCustomAgreement && !showSpecialConditionsRef.current) setValue('specialConditions', '');
  }, [isCustomAgreement, setValue]);

  return (
    <div className={sectionStyle}>
      {isCustomAgreement ? (
        <FormCustomAgreement form={form} required={required} />
      ) : (
        <Controller
          control={control}
          name="condition"
          render={({ field: { name, onChange, ref, value: condition }, fieldState: { error, invalid, isDirty, isTouched } }) => {
            const isCustomCondition = condition?.type === 'customCondition';

            const radioValue = match(condition)
              .with({ type: 'condition' }, ({ id }) => `${CONDITION_PREFIX}${id}`)
              .with({ type: 'customCondition' }, () => CUSTOM_CONDITION_VALUE)
              .otherwise(() => null);

            const onRadioChange = (next: unknown) => {
              if (next === CUSTOM_CONDITION_VALUE) {
                const [first] = customConditions;
                return onChange(first ? { id: first.id, type: 'customCondition' } : null);
              }

              if (typeof next === 'string' && next.startsWith(CONDITION_PREFIX)) {
                onChange({ id: Number(next.slice(CONDITION_PREFIX.length)), type: 'condition' });
              }
            };

            return (
              <Field.Root className={fieldStyle} dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label className={srOnlyStyle}>{t('invoices:fields.condition.label')}</Field.Label>

                <Radio.Group className={cardsRowStyle} disabled={isCustomAgreement} onValueChange={onRadioChange} value={radioValue}>
                  {conditions.map((option, index) => {
                    const descriptionKey = ({ Gebruiksrecht: 'usageRight', Overdracht: 'transfer' } as const)[option.name];

                    return (
                      <Radio.Root
                        key={option.id}
                        // The group itself isn't focusable, so the first card carries the field's ref — that's what
                        // React Hook Form focuses (and scrolls to) when a submit fails on this field.
                        ref={index === 0 ? ref : undefined}
                        value={`${CONDITION_PREFIX}${option.id}`}
                      >
                        <Radio.Label>{option.name}</Radio.Label>

                        {descriptionKey && (
                          <Radio.Description>{t(`invoices:createEdit.conditions.${descriptionKey}.description`)}</Radio.Description>
                        )}
                      </Radio.Root>
                    );
                  })}

                  {customConditionItems.length > 0 && (
                    <Radio.Root ref={conditions.length === 0 ? ref : undefined} value={CUSTOM_CONDITION_VALUE}>
                      <Radio.Label>{t('invoices:createEdit.conditions.customCondition.title')}</Radio.Label>
                      <Radio.Description>{t('invoices:createEdit.conditions.customCondition.description')}</Radio.Description>

                      <Field.Root className={cardSelectStyle}>
                        <Select.Root
                          items={customConditionItems}
                          onValueChange={(next) => next && onChange({ id: Number(next), type: 'customCondition' })}
                          value={isCustomCondition && condition ? String(condition.id) : null}
                        >
                          <Select.Button placeholder={t('invoices:createEdit.conditions.customCondition.placeholder')} />
                          <Select.List>
                            {customConditionItems.map(({ label, value }) => (
                              <Select.Item key={value} value={value}>
                                {label}
                              </Select.Item>
                            ))}
                          </Select.List>
                        </Select.Root>
                      </Field.Root>
                    </Radio.Root>
                  )}
                </Radio.Group>

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            );
          }}
          rules={{ required: { value: required, message: t('validation:required') } }}
        />
      )}

      {!isCustomAgreement && (
        <div className={actionsStyle}>
          {watchCondition?.type === 'condition' && (
            <Dialog.Root<number>>
              {({ payload }) => (
                <>
                  <Dialog.Trigger<number>
                    payload={watchCondition.id}
                    render={
                      <Button emphasis="tertiary" icon="File">
                        {t('invoices:createEdit.conditions.viewPdf')}
                      </Button>
                    }
                  />

                  {payload && <ConditionPdfDialog conditionId={payload} />}
                </>
              )}
            </Dialog.Root>
          )}

          <Button emphasis="tertiary" icon={showSpecialConditions ? 'CloseSmall' : 'Add'} onClick={toggleSpecialConditions}>
            {t('invoices:createEdit.conditions.specialConditionsAction')}
          </Button>
        </div>
      )}

      {(showSpecialConditions || isCustomAgreement) && (
        <Controller
          control={control}
          name="specialConditions"
          render={({ field: { name, onBlur, onChange, ref, value } }) => (
            <Field.Root disabled={isCustomAgreement} name={name}>
              <Field.Label optional>{t('invoices:fields.specialConditions.label')}</Field.Label>
              <Textarea
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('invoices:fields.specialConditions.placeholder')}
                ref={ref}
                rows={3}
                value={value}
              />
            </Field.Root>
          )}
        />
      )}
    </div>
  );
};
